import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BookingCategoryType } from '@com-delta-idp/mnx-sitewide-header-ui';
import { CommonService } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { UiStateManagementService } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { PrimaryIdWithoutBook } from '../../../../data-access/constants/navigation-links/navigation-linkts-constants';
import { AlertAdvisoriesFacade } from '../../../../data-access/facades/alert-advisories/alert-advisories.facade';
import { HeaderSlimFacadeService } from '../../../../data-access/facades/header-slim/header-slim-facade.service';
import {
  BOOKING_CATEGORY_TYPE,
  NavigationType
} from '../../../../data-access/models';
import { ConfigurationService } from './configuration.service';
import { ScreenDetectionService } from './screen-detection.service';

@Injectable({
  providedIn: 'root'
})
export class HeaderStateService {
  hamburgerMenuOpen = false;
  showLogo = true;
  selectedNav = signal('book');
  navClickTimestamp = signal(Date.now());
  previousPrimarySelectedNav = '';
  secondaryNav = '';
  mobileWidgetNavClick = signal(false);
  isLightHeaderFooterEnable = false;
  modifyButtonClick = signal(false);
  selectedBookingTab = signal<BookingCategoryType>('flights');
  bookingCategoryIndex = 0;
  displayCloseIcon = signal(false);
  switchAccountToggle = false;
  showInterstitialScreen = signal(false);
  restrictProfileSelector = false;
  collapsedHeader = false;

  loadedComponents = {
    bookingTab: false,
    flights: false,
    stays: false,
    cars: false,
    vacations: false,
    checkIn: false,
    myTrips: false,
    flightStatus: false
  };

  readonly navigationType = NavigationType;
  readonly bookingCategoryType = BOOKING_CATEGORY_TYPE;

  readonly commonService = inject(CommonService);
  readonly alertAdvisoryFacade = inject(AlertAdvisoriesFacade);
  readonly headerSlimFacade = inject(HeaderSlimFacadeService);
  readonly uiStateManagementService = inject(UiStateManagementService);
  private readonly screenDetection = inject(ScreenDetectionService);
  private readonly configuration = inject(ConfigurationService);

  viewSearchOptionExpanded = toSignal(
    this.uiStateManagementService.expandView$,
    { initialValue: false }
  );

  isMultiCityTrip = computed(
    () => this.headerSlimFacade.tripType() === 'MULTICITY'
  );

  alertBanner = computed(() =>
    this.alertAdvisoryFacade.showAlertAdvisoriesHeader()
  );

  toggleMenuState(): void {
    this.hamburgerMenuOpen = !this.hamburgerMenuOpen;
  }

  setBookingTabValue(event: BookingCategoryType): void {
    this.selectedBookingTab.set(event);
  }

  resetExpandPanel(closedFromButton: boolean): void {
    this.resetPanelState();
    if (closedFromButton) {
      this.uiStateManagementService.setExpandView(false);
      this.mobileWidgetNavClick.set(false);
    }
    if (
      PrimaryIdWithoutBook.includes(this.selectedNav()) &&
      this.screenDetection.isMobile()
    ) {
      this.selectedNav.set(NavigationType.book);
    }
  }

  private resetPanelState(): void {
    this.hamburgerMenuOpen = false;
    this.displayCloseIcon.set(false);
  }

  checkRestrictProfileSelector(): void {
    this.restrictProfileSelector = this.commonService.hidingProfileSelector();
  }

  isNotSecondaryNav = computed(
    () => this.selectedNav() !== this.navigationType.secondary
  );

  isTripRelatedNav = computed(() =>
    [
      this.navigationType.myTrips,
      this.navigationType.checkIn,
      this.navigationType.flightStatus
    ].includes(this.selectedNav() as NavigationType)
  );

  shouldShowMobileFullScreen = computed(
    () =>
      (this.mobileWidgetNavClick() || this.modifyButtonClick()) &&
      this.screenDetection.isMobile()
  );

  shouldCollapse = (shopHeader: boolean) =>
    computed(
      () =>
        (!this.configuration.APP_DISABLE_LEGACY && this.selectedNav() === '') ||
        (!this.modifyButtonClick() && shopHeader)
    );

  triggerNavClick(): void {
    this.navClickTimestamp.set(Date.now());
  }

  setModifyButtonClick(value: boolean): void {
    this.selectedNav.set('flights');
    this.setBookingTabValue('flights');
    this.modifyButtonClick.set(value);
    this.mobileWidgetNavClick.set(value);
    this.uiStateManagementService.setExpandView(value);
    if (!value) {
      this.displayCloseIcon.set(false);
    }
  }

  setDisplayCloseIcon(shopHeader: boolean): void {
    if (!this.modifyButtonClick() || !shopHeader)
      this.displayCloseIcon.set(true);
  }
}
